import { GraduationCap, User } from 'lucide-react';
import { useAIChat, type AIChatMessage } from '@/hooks/useAIChat';
import { AIChatInput } from './AIChatInput';
import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

interface HallyChatProps {
  className?: string;
  /** Compact mode - reduced height (30% smaller) */
  compact?: boolean;
}

export function HallyChat({ className, compact = false }: HallyChatProps) {
  const {
    messages,
    isLoading,
    isStreaming,
    streamingContent,
    sendMessageStreaming,
    cancelStream,
  } = useAIChat({ contextType: 'dashboard' });

  const bottomRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent]);

  return (
    <div
      className={cn(
        'rounded-3xl bg-brand-surface border border-brand-border flex flex-col overflow-hidden',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-brand-border flex-shrink-0">
        <div className="p-2.5 rounded-xl bg-gradient-to-br from-brand-primary/20 to-brand-secondary/20">
          <GraduationCap className="w-6 h-6 text-brand-primary" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-white">Hally</h2>
          <p className="text-xs text-brand-muted">Your AI Dating Coach</p>
        </div>
      </div>

      {/* Messages Area */}
      <div className={cn(
        "flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar",
        compact ? "min-h-[140px] max-h-[210px]" : "min-h-[200px] max-h-[300px]"
      )}>
        {messages.length === 0 && !isStreaming ? (
          <HallyEmptyState compact={compact} />
        ) : (
          <>
            {messages.map((message: AIChatMessage) => (
              <HallyMessageBubble key={message.id} message={message} />
            ))}

            {/* Streaming message */}
            {isStreaming && streamingContent && (
              <div className="flex gap-2 items-start">
                <div className="w-7 h-7 rounded-full bg-brand-primary/20 flex items-center justify-center flex-shrink-0">
                  <GraduationCap className="w-3.5 h-3.5 text-brand-primary" />
                </div>
                <div className="flex-1 bg-brand-background border border-brand-border rounded-2xl rounded-tl-md px-4 py-2">
                  <p className="text-sm text-white whitespace-pre-wrap">
                    {streamingContent}
                    <span className="inline-block w-1.5 h-4 ml-0.5 bg-brand-primary animate-pulse" />
                  </p>
                </div>
              </div>
            )}

            {/* Loading indicator */}
            {isStreaming && !streamingContent && (
              <div className="flex gap-2 items-start">
                <div className="w-7 h-7 rounded-full bg-brand-primary/20 flex items-center justify-center flex-shrink-0">
                  <GraduationCap className="w-3.5 h-3.5 text-brand-primary" />
                </div>
                <div className="bg-brand-background border border-brand-border rounded-2xl rounded-tl-md px-4 py-3">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-brand-muted rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-brand-muted rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-brand-muted rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}

            <div ref={bottomRef} />
          </>
        )}
      </div>

      {/* Input */}
      <AIChatInput
        onSend={sendMessageStreaming}
        onCancel={cancelStream}
        isLoading={isLoading}
        isStreaming={isStreaming}
        placeholder="Ask Hally anything about dating..."
      />
    </div>
  );
}

function HallyEmptyState({ compact = false }: { compact?: boolean }) {
  return (
    <div className={cn(
      "flex flex-col items-center justify-center h-full text-center",
      compact ? "py-2" : "py-6"
    )}>
      <div className={cn(
        "rounded-full bg-gradient-to-br from-brand-primary/20 to-brand-secondary/20 flex items-center justify-center",
        compact ? "w-10 h-10 mb-2" : "w-14 h-14 mb-3"
      )}>
        <GraduationCap className={compact ? "w-5 h-5 text-brand-primary" : "w-7 h-7 text-brand-primary"} />
      </div>
      <h3 className={cn(
        "font-semibold text-white",
        compact ? "text-sm mb-0.5" : "text-base mb-1"
      )}>
        Hi! I'm Hally
      </h3>
      <p className={cn(
        "text-brand-muted max-w-xs",
        compact ? "text-xs" : "text-sm"
      )}>
        {compact
          ? "Ask me about your matches or dating advice!"
          : "Your AI dating coach. Ask me about your matches, get conversation tips, or dating advice!"
        }
      </p>
      <div className={cn(
        "flex flex-wrap justify-center gap-2",
        compact ? "mt-2" : "mt-4"
      )}>
        <SuggestionChip text="Who should I message first?" compact={compact} />
        <SuggestionChip text="Dating tips" compact={compact} />
        {!compact && <SuggestionChip text="Summarize my connections" />}
      </div>
    </div>
  );
}

function HallyMessageBubble({ message }: { message: AIChatMessage }) {
  const isUser = message.role === 'user';

  return (
    <div className={cn('flex gap-2 items-start', isUser && 'flex-row-reverse')}>
      <div
        className={cn(
          'w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0',
          isUser ? 'bg-brand-primary' : 'bg-brand-primary/20'
        )}
      >
        {isUser ? (
          <User className="w-3.5 h-3.5 text-white" />
        ) : (
          <GraduationCap className="w-3.5 h-3.5 text-brand-primary" />
        )}
      </div>

      <div
        className={cn(
          'flex-1 max-w-[85%] rounded-2xl px-4 py-2',
          isUser
            ? 'bg-brand-primary text-white rounded-tr-md ml-auto'
            : 'bg-brand-background border border-brand-border text-white rounded-tl-md'
        )}
      >
        <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
      </div>
    </div>
  );
}

function SuggestionChip({ text, compact = false }: { text: string; compact?: boolean }) {
  return (
    <button className={cn(
      "text-brand-muted bg-brand-background border border-brand-border rounded-full hover:border-brand-primary hover:text-white transition-colors",
      compact ? "px-2 py-1 text-[10px]" : "px-3 py-1.5 text-xs"
    )}>
      {text}
    </button>
  );
}
